import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { DefectData } from 'src/lib/fliko/models/defects/defect.data';
import { IssueData } from 'src/lib/fliko/models/defects/issue.data';
import { TranslateMessageRequest } from 'src/lib/fliko/models/http/translation.data';
import { NewsData } from 'src/lib/fliko/models/news/news.data';
import { Language, TrObjectType, TrKey, TrMessageType } from 'src/lib/fliko/models/translation/generic';
import { Collection } from 'src/lib/fliko/types/database.type';
import { Nullish } from 'src/lib/fliko/types/typeish.type';
import { TranslateMessageRequestVM } from '../models/translation-view.model';
import { FunctionsApiService, Result } from './functions-api.service';
import { LocalizationService } from './localization.service';
import { DatabaseService } from './database.service';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  constructor(
    private dbSrv: DatabaseService,
    private fas: FunctionsApiService,
    private ls: LocalizationService
  ) {}

  /**
   * @param {TrKey}  type
   * @param {string} field
   * @param {string} id
   * @path /trSourceLang/$estateId/$type/$field/$id/
   */
  private sourceLanguage = (type: TrKey, field: string, id: string) =>
    this.dbSrv.estateFactory.getObject<Language>(`trSourceLang/$eId/${type}/${field}/${id}`);

  /**
   * @param {TrKey}  type
   * @param {string} field
   * @path /trSourceLang/$estateId/$type/$field/
   */
  private sourceLanguages = (type: TrKey, field: string) =>
    this.dbSrv.estateFactory.getCollection<Language>(`trSourceLang/$eId/${type}/${field}`);

  /* Tr collection |  field values
    ---------------|----------------
          news     | 'title', 'description'
        defect     | 'title', 'description'
        resIssue   | 'title', 'description'
      defectNote   | $defectId
      chatMessage  | $conversationId
  */

  /**
   * @param {TrKey}    type
   * @param {string}   field
   * @param {string}   id
   * @param {Language} language
   * @path /translations/$estateId/$type/$field/$language/$id/
   */
  private translatedField = (type: TrKey, field: string, id: string, language: Language) =>
    this.dbSrv.estateFactory.getObject<string>(`translations/$eId/${type}/${field}/${language}/${id}`);

  /**
   * @param {TrKey}    type
   * @param {string}   field
   * @param {Language} language
   * @path /translations/$estateId/$type/$field/$language/
   */
  private translatedFields = (type: TrKey, field: string, language: Language) =>
    this.dbSrv.estateFactory.getCollection<string>(`translations/$eId/${type}/${field}/${language}`);

  public getTitles$(type: TrObjectType, language: Language): Observable<Collection<string> | Nullish> {
    return this.translatedFields(type, 'title', language).getObject();
  }

  public getObject$(type: 'news', id: string, language: Language): Observable<Partial<DefectData> | null>;
  public getObject$(type: 'defect', id: string, language: Language): Observable<Partial<IssueData> | null>;
  public getObject$(type: 'residentIssue', id: string, language: Language): Observable<Partial<NewsData> | null>;

  public getObject$(type: TrObjectType, id: string, language: Language): Observable<Collection<string> | null>;

  public getObject$<T>(type: TrObjectType, id: string, language: Language): Observable<Partial<T> | Nullish> {
    return combineLatest([
      this.translatedField(type, 'title', id, language).get(),
      this.translatedField(type, 'description', id, language).get()
    ]).pipe(
      map(([title, description]) => {
        // HACK: TS2352: Conversion to type 'T' may be a mistake.
        // If intentional, convert expression to 'unknown' first.
        const obj = ObjHlp.replaceNullish({ title, description } as unknown as T);
        return ObjHlp.isEmpty(obj) ? null : obj;
      })
    );
  }

  public getConversation$(type: TrMessageType, id: string, language: Language): Observable<Collection<string> | Nullish> {
    return this.translatedFields(type, id, language).getObject();
  }

  public getLanguages$(type: TrKey, field: string): Observable<Collection<Language> | Nullish> {
    return this.sourceLanguages(type, field).getObject();
  }

  public getLanguage$(type: TrObjectType, id: string): Observable<Language | Nullish> {
    // HACK: works as long, as all translated objects have 'description' and 'title' field
    return this.sourceLanguage(type, 'description', id)
      .get()
      .pipe(switchMap((lang) => (lang ? of(lang) : this.sourceLanguage(type, 'title', id).get())));
  }

  public translateMessage(request: TranslateMessageRequestVM, estateId: string): Result<void> {
    const rq: TranslateMessageRequest = {
      ...request,
      estateId,
      language: this.ls.currentLang as Language
    };
    return this.fas.callFunction('translateMessage', rq);
  }
}
